import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { FormulaContent } from './FormulaContent'
import { FormSectionTitle, PairingButton } from 'src/Libs/flow/components'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { HideDirtyStateProvider } from '../../providers'
import { MAX_Nominal_POWER, MAX_TOTAL_POWER_CONSUMPTION } from '../../constants'
import { useHtc320hIo } from '../hooks/use-htc-320h-io'
import { AutoCalculateButton } from './AutoCalculateButton'
import { SettingsBox } from 'src/Elements/Data'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import useAccordionStyle from 'src/Libs/styles'
import { NumberField, Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { UnitTypes } from 'src/Libs/types'
import { getPairingButtonLabel } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

export const Htc320hIo: React.FC = () => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { classes } = useAccordionStyle()

  const { displayName, isDirty, isError, showHtc320hIoInputs, smartMeterGatewayInfo, treatHtcAsNonSgReady, handleDelete, handlePairing, htc320h } =
    useHtc320hIo()

  const { isNew, isConsumptionActive, isProductionActive, nominalPower, pairingId, deviceId, navigationId } = htc320h ?? {}

  return (
    <HideDirtyStateProvider hideDirtyState={!!isNew}>
      <Box component={'section'} id={navigationId}>
        <AccordionItem
          title={<FormSectionTitle name={displayName} onClick={handleDelete} isDirty={isDirty} isError={isError} />}
          expanded
          className={classes.accordion}
          sx={{ margin: 0 }}
        >
          <Box sx={{ p: 2 }}>
            <SettingsBox
              title="installation.submeterEditor.deviceName"
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <TextField
                    label={t('installation.submeterEditor.deviceName')}
                    name={'ioInterfaces.htc320h.name'}
                    maxLength={100}
                    fullWidth
                    disabled={!canEdit}
                  />
                </Box>
              }
            />

            <Stack spacing={2} mb={2} component="section">
              <Typography
                component="h3"
                sx={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  pb: 2,
                  pt: 3,
                  color: theme => theme.palette.primary.dark,
                  mb: 2,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                }}
              >
                {t('installation.ioInterfaceEditor.controller')}
              </Typography>

              <SettingsBox
                title={'installation.productEditor.controllerDevice'}
                suffix={
                  <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    <ShellTextField disabled label={t('installation.productEditor.controllerDevice')} value={t('installation.types.modbusRTU')} />
                  </Box>
                }
              />

              <SettingsBox
                title={'productEditor.pairingEditor.pairingID.htc'}
                suffix={
                  <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    <TextField name={'ioInterfaces.htc320h.pairingId'} label={t('productEditor.pairingEditor.pairingID.htc')} disabled fullWidth />
                  </Box>
                }
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <PairingButton size="large" sx={{ height: '100%', minWidth: { xs: '100%', sm: 'auto' } }} onClick={handlePairing} icon={'transfer'}>
                  {getPairingButtonLabel(t, deviceId, pairingId)}
                </PairingButton>
              </Box>

              {showHtc320hIoInputs && (
                <>
                  <SettingsBox
                    title={'installation.submeterEditor.input.consumption.label'}
                    suffix={
                      <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                        <Select
                          name="ioInterfaces.htc320h.isConsumptionActive"
                          label={t('installation.submeterEditor.input.consumption.label')}
                          options={[
                            {
                              value: true,
                              label: t('installation.submeterEditor.input.inputType.consumption'),
                            },
                            {
                              value: false,
                              label: t('installation.submeterEditor.input.inputType.notUsedConsumption'),
                            },
                          ]}
                          disabled={!canEdit}
                        />
                      </Box>
                    }
                  />

                  {isConsumptionActive && (
                    <Stack spacing={2} position={'relative'}>
                      <SettingsBox
                        title={'installation.submeterEditor.input.production.value'}
                        suffix={
                          <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                            <NumberField
                              name={'ioInterfaces.htc320h.totalPowerConsumption'}
                              label={t('installation.submeterEditor.input.production.value')}
                              suffix={t('affixes.watt')}
                              min={0}
                              max={MAX_TOTAL_POWER_CONSUMPTION}
                              fullWidth
                              disabled={!canEdit}
                            />
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <AutoCalculateButton />
                      </Box>

                      <Typography py={1.5}>{t('installation.submeterEditor.input.consumption.info')}</Typography>

                      <FormulaContent nominalPower={nominalPower || 0} />
                    </Stack>
                  )}
                  <SettingsBox
                    title={'installation.submeterEditor.input.production.label'}
                    suffix={
                      <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                        <Select
                          name="ioInterfaces.htc320h.isProductionActive"
                          label={t('installation.submeterEditor.input.production.label')}
                          options={[
                            {
                              value: true,
                              label: t('installation.submeterEditor.input.inputType.production'),
                            },
                            {
                              value: false,
                              label: t('installation.submeterEditor.input.inputType.notUsedProduction'),
                            },
                          ]}
                          disabled={!canEdit}
                        />
                      </Box>
                    }
                  />

                  {isProductionActive && !!smartMeterGatewayInfo?.smartMeterGateway.active && (
                    <Typography px={3} pt={1.5}>
                      {t('installation.submeterEditor.input.production.info')}
                    </Typography>
                  )}
                </>
              )}
            </Stack>

            <Box component="section">
              <Typography
                component="h3"
                sx={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  pb: 2,
                  pt: 3,
                  color: theme => theme.palette.primary.dark,
                  mb: 2,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                }}
              >
                {t('installation.ioInterfaceEditor.controlledDevice')}
              </Typography>

              {treatHtcAsNonSgReady ? (
                <SettingsBox
                  title={'installation.submeterEditor.outputsConfig'}
                  suffix={
                    <Box>
                      <ShellTextField
                        label={t('installation.submeterEditor.outputsConfig')}
                        value={t('installation.submeterEditor.input.inputType.notUsedConsumption')}
                        disabled
                      />
                    </Box>
                  }
                />
              ) : (
                <Stack spacing={2}>
                  <SettingsBox
                    title={'installation.submeterEditor.outputsConfig'}
                    suffix={
                      <Box>
                        <ShellTextField
                          label={t('installation.submeterEditor.outputsConfig')}
                          value={t('installation.submeterEditor.outputConfig.sgReady')}
                          disabled
                        />
                      </Box>
                    }
                  />

                  <SettingsBox
                    title={'installation.submeterEditor.deviceType'}
                    suffix={
                      <Box>
                        <ShellTextField
                          label={t('installation.submeterEditor.deviceType')}
                          value={t('installation.submeterEditor.deviceTypeOptions.hvac')}
                          disabled
                        />
                      </Box>
                    }
                  />
                  <SettingsBox
                    title={'installation.submeterEditor.nominalPower'}
                    suffix={
                      <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                        <NumberField
                          label={t('installation.submeterEditor.nominalPower')}
                          suffix={UnitTypes.watt}
                          name={'ioInterfaces.htc320h.nominalPower'}
                          min={0}
                          max={MAX_Nominal_POWER}
                          fullWidth
                          disabled={!canEdit}
                        />
                      </Box>
                    }
                  />
                </Stack>
              )}
            </Box>
          </Box>
        </AccordionItem>
      </Box>
    </HideDirtyStateProvider>
  )
}
